import { EditOutlined, CheckCircleOutlined, PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from '@tanstack/react-router';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useDestinations, useDeleteDestination } from '_/hooks/useDestinations';
import useOrgAccess from '_/hooks/useOrgAccess';
import { SOURCE_ENGINEER_NOT_INCLUDED } from '_/utils/userManagement';
import { Table, Button, Tooltip, Modal, Space, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, { ReactElement, useState, useEffect, useCallback } from 'react';

interface Destination {
  id: string;
  name: string;
  destinationId?: string;
  type: string;
  active: boolean;
  encryptedConfig?: any;
}

function OrganizationDestinations(): ReactElement {
  const access = useOrgAccess(SOURCE_ENGINEER_NOT_INCLUDED);
  const history = useNavigate();
  const { selectedOrgSlug, selectedOrganization } = useOrganization();
  const { data: destinations = [], refetch, isFetching } = useDestinations();
  const { mutate: deleteDestinationMutation } = useDeleteDestination();
  const { setBreadcrumbs, setActionButtons, setOnRefresh, setRefreshLoading } = useTopHeader();

  const [searchText, setSearchText] = useState('');
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState<Destination | null>(null);

  const handleNewDestination = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      void history({ to: `/orgs/${selectedOrgSlug}/destinations/_new` });
    },
    [history, selectedOrgSlug],
  );

  const handleDestinationClose = () => {
    setIsDeleteModalVisible(false);
    setSelectedDestination(null);
  };

  const handleDestinationConfirm = () => {
    if (selectedDestination) {
      deleteDestinationMutation(selectedDestination.id);
      setIsDeleteModalVisible(false);
      setSelectedDestination(null);
    }
  };

  const handleEditDestination = (destination: Destination) => {
    void history({ to: `/orgs/${selectedOrgSlug}/destinations/${destination.name}/edit` });
  };

  // Set up header with breadcrumbs and action buttons
  useEffect(() => {
    setBreadcrumbs([
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${selectedOrgSlug}` },
      { title: 'Destinations' },
    ]);
    setActionButtons([
      {
        onClick: handleNewDestination,
        icon: <PlusCircleOutlined />,
        label: 'Add Destination',
        disabled: !access,
      },
    ]);
    setOnRefresh(refetch);

    return () => {
      setBreadcrumbs([]);
      setActionButtons([]);
      setOnRefresh(undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setBreadcrumbs, setActionButtons, setOnRefresh, selectedOrgSlug, selectedOrganization, access, handleNewDestination]);

  // Update refresh loading state
  useEffect(() => {
    setRefreshLoading(isFetching);
  }, [isFetching, setRefreshLoading]);

  // Filter destinations based on search text
  const filteredDestinations = destinations?.filter((destination) => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    return (
      destination.name?.toLowerCase().includes(searchLower) ||
      destination.type?.toLowerCase().includes(searchLower) ||
      destination.destinationId?.toLowerCase().includes(searchLower)
    );
  });

  const columns: ColumnsType<Destination> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      sorter: (a, b) => a.type.localeCompare(b.type),
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      width: 100,
      align: 'center',
      render: (active: boolean) => (active ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} /> : null),
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <Tooltip title={access ? 'Edit destination' : 'You do not have permission to edit'}>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleEditDestination(record);
            }}
            disabled={!access}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Input
        placeholder="Search destinations..."
        prefix={<SearchOutlined />}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ width: 300 }}
        allowClear
      />
      <Table
        columns={columns}
        dataSource={filteredDestinations}
        rowKey="id"
        pagination={{
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} destinations`,
        }}
      />
      <Modal
        open={isDeleteModalVisible}
        title="Delete Destination"
        onCancel={handleDestinationClose}
        onOk={handleDestinationConfirm}
      >
        <p>Are you sure that you want to delete this destination?</p>
      </Modal>
    </Space>
  );
}

export default OrganizationDestinations;
